import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { ScaledSheet } from '@utils/scale';
import WeeklyDatePicker from '../components/WeeklyDatePicker';
import EarningsCard from '../components/EarningsCard';
import IncentiveProgress from '../components/IncentiveProgress';
import StatsCard from '../components/StatsCard';
import BreakdownList from '../components/BreakdownList';
import TransactionHistoryLink from '../components/TransactionHistoryLink';
import IncentiveReceived from '../components/IncentiveReceived';
import IncentiveUnlocked from '../components/IncentiveUnlocked';
import IncentiveMissed from '../components/IncentiveMissed';
import LineChartCard from '../components/LineChartCard';
import { getWeeklyEarnings } from '@services/api';
import { checkIfDateIsWeekKey, formatWeekKey, getDateFromWeekKey } from '../utils';

const GRADIENT_COLORS = ['#FFFFFF', '#F5F5F5', '#E8E8E8'];
const GRADIENT_LOCATIONS = [0, 0.7, 1];
const LOADING_INDICATOR_COLOR = '#000';
const EXPECTED_EARNINGS_SUMMARY_LENGTH = 3;
const CHART_MODE_LABEL = 'Weekly';

interface DateFilter {
  key: string;
  value: string;
}

interface EarningsGraphItem {
  date: string;
  value: number;
  status: string;
  title: string;
  highlight?: boolean;
}

interface EarningsGraph {
  title: string;
  month_txt: string;
  list: EarningsGraphItem[];
  sublist?: Array<{
    date: string;
    sub_value: number;
    sub_value_title: string;
  }>;
}

interface WeeklyEarningsData {
  selected_filter?: DateFilter;
  date_filter_options?: DateFilter[];
  recon_summary?: {
    date_range_txt?: string;
  };
  earnings_graph?: EarningsGraph;
  incentive_summary?: {
    type: string;
    text: string;
  };
  earnings_summary?: unknown[];
  earnings_transactions?: unknown[];
  footer?: {
    separator?: string;
    buttons?: Array<{ title: string }>;
  };
}

const getTodayFromGraph = (graphData?: EarningsGraph): string | null => {
  if (!graphData?.list) return null;
  const todayData = graphData.list.find((item) => item.highlight);
  return todayData?.date || null;
};

const transformEarningsGraphData = (graphData?: EarningsGraph) => {
  if (!graphData?.list) return null;

  return {
    incomes: graphData.list.map((item) => ({
      date: item.date,
      amount: item.value,
      status: item.status,
    })),
    dayInitials: graphData.list.map((item) => item.title),
    payout: graphData.sublist?.[0]
      ? {
          date: graphData.sublist[0].date,
          amount: graphData.sublist[0].sub_value,
          label: graphData.sublist[0].sub_value_title,
        }
      : null,
  };
};

const WeeklyTabContainer: React.FC<{ date: string }> = ({ date }) => {
  const navigation = useNavigation();
  const defaultDate = checkIfDateIsWeekKey(date) ? date : '';
  const defaultDateValue = defaultDate && formatWeekKey(defaultDate) || '';
  console.log(defaultDate, defaultDateValue);

  const [selectedDate, setSelectedDate] = useState<DateFilter>({ key: defaultDate, value: defaultDateValue });
  const [earningsData, setEarningsData] = useState<WeeklyEarningsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const loadEarningsData = useCallback(async (dateRange: string = '') => {
    try {
      setLoading(true);
      const { data } = await getWeeklyEarnings(dateRange);
      setEarningsData(data);
      if (data?.selected_filter) {
        setSelectedDate(data.selected_filter);
      }
    } catch (error) {
      console.error('Error loading weekly earnings data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadEarningsData(selectedDate.key);
    }, [loadEarningsData, selectedDate.key]),
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadEarningsData(selectedDate.key);
    setRefreshing(false);
  }, [loadEarningsData, selectedDate.key]);

  const handleDateChange = useCallback((date: DateFilter) => {
    setSelectedDate(date);
  }, []);

  const handleViewTransactionHistory = useCallback(() => {
    // @ts-ignore - Navigation typing
    navigation.navigate('TransactionStatements', { date_range: selectedDate.key });
  }, [navigation, selectedDate.key]);

  // Memoize computed values
  const chartData = useMemo(
    () => transformEarningsGraphData(earningsData?.earnings_graph),
    [earningsData?.earnings_graph],
  );

  const todayDate = useMemo(
    () => getTodayFromGraph(earningsData?.earnings_graph),
    [earningsData?.earnings_graph],
  );

  const hasValidEarningsSummary = useMemo(
    () =>
      Array.isArray(earningsData?.earnings_summary) &&
      earningsData.earnings_summary.length === EXPECTED_EARNINGS_SUMMARY_LENGTH,
    [earningsData?.earnings_summary],
  );

  const hasEarningsGraph = useMemo(
    () => earningsData?.earnings_graph?.list && earningsData.earnings_graph.list.length > 0,
    [earningsData?.earnings_graph],
  );

  if (loading && !earningsData) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={LOADING_INDICATOR_COLOR} />
      </View>
    );
  }

  const incentiveStatus = earningsData?.incentive_summary?.type;

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      <LinearGradient
        colors={GRADIENT_COLORS}
        style={styles.mainCard}
        locations={GRADIENT_LOCATIONS}
      >
        <WeeklyDatePicker
          dateRangeOptions={earningsData?.date_filter_options || []}
          dateRangeText={earningsData?.recon_summary?.date_range_txt || ''}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          reportType="WEEKLY"
        />

        {earningsData?.recon_summary && (
          <EarningsCard reconSummary={earningsData.recon_summary as any} />
        )}
      </LinearGradient>
      {hasEarningsGraph && chartData && (
        <View style={styles.chartContainer}>
          <LineChartCard
            title={earningsData?.earnings_graph?.title}
            monthLabel={earningsData?.earnings_graph?.month_txt}
            modeLabel={CHART_MODE_LABEL}
            today={todayDate ? new Date(todayDate) : undefined}
            incomes={chartData.incomes}
            payout={chartData.payout}
            dayInitials={chartData.dayInitials}
          />
        </View>
      )}
      {(incentiveStatus === 'before_first_milestone') && (
        <View>
          <IncentiveProgress
            htmlContent={earningsData?.incentive_summary?.text || ''}
          />
        </View>
      )}
      {(incentiveStatus === 'incentive_received') && (
        <View>
          <IncentiveReceived
            htmlContent={earningsData?.incentive_summary?.text || ''}
          />
        </View>
      )}
      {(incentiveStatus === 'unlocked_milestone_next') && (
        <View>
          <IncentiveUnlocked htmlContent={earningsData?.incentive_summary?.text || ''} />
        </View>
      )}
      {(incentiveStatus === 'missed_first_milestone') && (
        <View>
          <IncentiveMissed htmlContent={earningsData?.incentive_summary?.text || ''} />
        </View>
      )}
      {hasValidEarningsSummary && earningsData?.earnings_summary && (
        <View style={styles.cardContainer}>
          <StatsCard
            earningsPerOrder={earningsData.earnings_summary[0] as { title: string; subtitle: string }}
            totalOrders={earningsData.earnings_summary[1] as { title: string; subtitle: string }}
            totalEarnings={earningsData.earnings_summary[2] as { title: string; subtitle: string }}
          />
        </View>
      )}
      <BreakdownList 
        data={earningsData?.earnings_transactions as any} 
        date={getDateFromWeekKey(selectedDate?.key || '')}
        date_range={selectedDate?.key || ''}
      />
      {earningsData?.footer?.separator && (
        <View style={styles.dividerContainer}>
          <View style={styles.divider} />
          <View style={styles.dividerTextContainer}>
            <Text style={styles.dividerText}>{earningsData.footer.separator}</Text>
          </View>
          <View style={styles.divider} />
        </View>
      )}
      {earningsData?.footer?.buttons?.[0]?.title && (
        <TransactionHistoryLink
          title={earningsData.footer.buttons[0].title}
          onPress={handleViewTransactionHistory}
        />
      )}
    </ScrollView>
  );
};

const styles = ScaledSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: '#EDF0F3',
  },
  scrollContent: {
    paddingBottom: 24,
    backgroundColor: '#EDF0F3',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#EDF0F3',
  },
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginHorizontal: 12,
    marginTop: 12,
    marginBottom: 12,
    paddingTop: 24,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderWidth: 4,
    borderColor: '#FFF',
  },
  incentiveProgressContainer: {
    marginBottom: 12,
    marginHorizontal: 12,
  },
  chartContainer: {
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  cardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginHorizontal: 12,
    marginBottom: 12,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
    marginBottom: 12,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: '#E0E0E0',
  },
  dividerTextContainer: {
    paddingHorizontal: 12,
  },
  dividerText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#9E9E9E',
    letterSpacing: 0.5,
  },
});

export default WeeklyTabContainer;

